/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import Card from "@mui/material/Card";
import Grid from "components/generals/Grid";

import {
  AppointmentInterface,
  HeaderEnum,
  PatientInterface,
  ProgressNoteInterface,
  QueryResultInterface,
  VisitStatusEnum,
} from "medicalsystem-shared";
import Typography from "components/generals/Typography";
import Box from "components/generals/Box";
import useHttp from "hooks/http.hook";
import { useEffect, useState } from "react";
import { CoreAPI_URLs } from "constants/http.const";
import { Caption, H4 } from "components/generals/_Titles";
import { getFromISOYYYYMMDDDateFormat } from "utils/date.utils";
import Button from "components/generals/Button";
import { useNavigate } from "react-router-dom";
import { RoutesConstants } from "medicalsystem-shared";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";
import { useTranslation } from "react-i18next";

/**
 * Properties for the AppointmentWidget component.
 * 
 * @interface AppointmentWidgetProps
 * @property {Partial<PatientInterface>} [patientSelected] - Selected patient to display appointments for
 */
interface AppointmentWidgetProps {
  patientSelected?: Partial<PatientInterface>;
}

/**
 * Appointment widget component for patient appointment summary.
 * Displays last and next appointments with quick access to progress notes and new appointments.
 * 
 * Features:
 * - Last appointment display with date and provider
 * - Next appointment display with date and provider
 * - Quick access to progress note from last appointment
 * - Create new appointment button
 * - Permission-based new appointment access
 * - Automatic appointment loading on patient selection
 * - Progress note availability check
 * - Navigation to progress note view
 * - Navigation to new appointment form
 * - i18n translation support
 * - Responsive card layout
 * 
 * Appointment Display:
 * - Last Appointment: Most recent non-check-in appointment
 * - Next Appointment: Upcoming check-in status appointment
 * - Shows date and provider name for each
 * - "No Data" message if no appointments exist
 * 
 * Progress Note Access:
 * - Button to view progress note from last appointment
 * - Disabled if no progress note exists
 * - Navigates to progress note view with appointment context
 * - Automatically checks progress note availability
 * 
 * New Appointment:
 * - Button to create new appointment
 * - Pre-fills with selected patient data
 * - Permission-controlled visibility
 * - Navigates to appointment form
 * 
 * Data Loading:
 * - Fetches all patient appointments on patient selection
 * - Filters by visit status to determine last/next
 * - Loads progress note for last appointment
 * - Updates on patient change
 * 
 * Visit Status Filtering:
 * - Last: Excludes CHECK_IN status (completed visits)
 * - Next: Only CHECK_IN status (scheduled visits)
 * - Takes most recent from filtered results
 * 
 * @param {Readonly<AppointmentWidgetProps>} props - Component properties
 * @returns {JSX.Element} Appointment widget card
 */
function AppointmentWidget({ patientSelected }: Readonly<AppointmentWidgetProps>): JSX.Element {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [lastAppointment, setLastAppointment] = useState<AppointmentInterface>();
  const [appointmentList, setAppointmentList] = useState<AppointmentInterface[]>([]);
  const [nextAppointment, setNextAppointment] = useState<AppointmentInterface>();
  const [progressNote, setProgressNote] = useState<Partial<ProgressNoteInterface>>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  useEffect(() => {
    if (patientSelected) {
      sendRequest(CoreAPI_URLs.APPOINTMENTS_GET_ALL, onGetAppointments, "GET", undefined, [
        {
          header: HeaderEnum.PATIENT_ID,
          value: patientSelected.id,
        },
      ]);
    }
  }, [patientSelected]);

  /**
   * Callback handler for appointments data retrieval.
   * Processes appointment list and identifies last and next appointments.
   * 
   * Last Appointment Logic:
   * - Filters out CHECK_IN status appointments
   * - Takes last appointment from filtered results
   * - Represents most recent completed visit
   * 
   * Next Appointment Logic:
   * - Filters for CHECK_IN status only
   * - Takes last appointment from filtered results
   * - Represents upcoming scheduled visit
   * 
   * Also triggers progress note fetch for last appointment if available.
   * 
   * @param {QueryResultInterface<AppointmentInterface>} data - Query result with appointments array
   * @returns {void}
   */
  const onGetAppointments = (data: QueryResultInterface<AppointmentInterface>) => {
    if (data.results) {
      setAppointmentList(data.results);
  
      const lastAppointmentData =
        data.results
          .filter((appointment) => appointment.visitStatus !== VisitStatusEnum.CHECK_IN)
          .pop() || null;
  
      const nextAppointmentData =
        data.results
          .filter((appointment) => appointment.visitStatus === VisitStatusEnum.CHECK_IN)
          .pop() || null;
          
      setLastAppointment(lastAppointmentData);
      setNextAppointment(nextAppointmentData);
      
      if (lastAppointmentData && lastAppointmentData.id) {
        sendRequest(
          CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
          onGetAppointmentProgressNote,
          "GET",
          undefined,
          [
            {
              header: HeaderEnum.RESOURCE_ID,
              value: lastAppointmentData.id,
            },
          ]
        );
      }
    }
  };
  
  /**
   * Navigates to progress note view for the last appointment.
   * Passes appointment ID and selection flag to progress note page.
   * 
   * @returns {void}
   */
  function handleNewProgressNote() {
    const data = lastAppointment.id;
    const selectedData = true;
    navigate(RoutesConstants.PROGRESS_NOTE, { state: { data, selected: selectedData } });
  }
  
  /**
   * Callback handler for appointment details with progress note.
   * Updates progress note state based on appointment data.
   * Sets null if no progress note exists (disables button).
   * 
   * @param {AppointmentInterface} appointmentData - Appointment data with progress note
   * @returns {void}
   */
  const onGetAppointmentProgressNote = (appointmentData: AppointmentInterface) => {
    if (appointmentData.progressNote) {
      setProgressNote(appointmentData.progressNote);
    } else {
      setProgressNote(null);
    }
  };
  return (
    <Card id="appointments">
      <Box p={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <H4>{t("appointmentWidget.appointments")}</H4>
          </Grid>
          {appointmentList ? (
            <>
              <Grid item xs={12}>
                <Box height="100%" mt={0.5} lineHeight={1}>
                  <Grid item xs={12}>
                    <Caption color="info">{t("appointmentWidget.lastAppointment")}: </Caption>
                    {lastAppointment ? (
                      <>
                        <Caption>
                          {getFromISOYYYYMMDDDateFormat(new Date(lastAppointment.arrivalDateTime))}
                        </Caption>
                        <Caption marginRight={4}>
                          , Dr {lastAppointment.provider.person.name}
                          {lastAppointment.provider.person.lastname}
                        </Caption>
                        <Button
                          variant="contained"
                          color="info"
                          onClick={handleNewProgressNote}
                          disabled={progressNote === null}
                        >
                          {t("appointmentWidget.progressNote")}
                        </Button>
                      </>
                    ) : (
                      <Caption color="info"> {t("appointmentWidget.noData")} </Caption>
                    )}
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box height="100%" mt={0.5} lineHeight={1}>
                  <Grid item xs={12}>
                    <Caption color="info">{t("appointmentWidget.nextAppointment")}: </Caption>
                    {nextAppointment ? (
                      <>
                        <Caption>
                          {getFromISOYYYYMMDDDateFormat(new Date(nextAppointment.arrivalDateTime))}
                        </Caption>
                        <Caption>
                          , Dr {nextAppointment.provider.person.name}
                          {nextAppointment.provider.person.lastname}
                        </Caption>
                      </>
                    ) : (
                      <Caption color="info">{t("appointmentWidget.noData")} </Caption>
                    )}
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} display={"flex"} justifyContent={"flex-start"}>
                <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.NEW_APPOINTMENT].VIEW]}
                >
                  <Button
                    variant="contained"
                    color="info"
                    onClick={() => {
                      navigate(RoutesConstants.NEW_APPOINTMENT, {
                        state: {
                          data: patientSelected,
                        },
                      });
                    }}
                  >
                    {t("appointmentWidget.newAppointment")}
                  </Button>
                </PermissionWrapper>
              </Grid>
            </>
          ) : (
            <Grid item>
              <Typography variant="h5" fontWeight="medium">
                {t("appointmentWidget.noAppointmentInformation")}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Card>
  );
}

export default AppointmentWidget;
